// ManagerSupport.jsx — same as InstructorSupport but sees ALL tickets
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Inbox as InboxIcon,
  CheckCircle as ResolvedIcon,
  HourglassEmpty as PendingIcon,
  Reply as ReplyIcon,
} from "@mui/icons-material";
import PageLayout from "../../components/pagelayout/PageLayout";
import ContentLayout from "../../components/pagelayout/ContentLayout";
import { getData, patchData } from "../../utils/backendApis";
import { useSnackbar } from "../../contexts/SnackbarContext";

const statusColor = {
  open: "warning",
  in_progress: "info",
  resolved: "success",
  closed: "default",
};

const RespondDialog = ({ ticket, open, onClose, onSuccess }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [response, setResponse] = useState("");
  const [newStatus, setNewStatus] = useState("resolved");
  const [loading, setLoading] = useState(false);
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    if (ticket) {
      setResponse(ticket.response || "");
      setNewStatus(ticket.status === "open" ? "resolved" : ticket.status);
    }
  }, [ticket]);

  const handleSubmit = async () => {
    if (!response.trim()) return;
    setLoading(true);
    try {
      await patchData(
        `api/support/tickets/${ticket.id}/respond/`,
        { response, status: newStatus },
        "Response sent.",
        "Failed to send response."
      );
      onSuccess();
      onClose();
    } catch (_) {
    } finally {
      setLoading(false);
    }
  };

  if (!ticket) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ fontWeight: 700 }}>Respond: {ticket.subject}</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, p: 1.5, bgcolor: "grey.50", borderRadius: 1, borderLeft: "3px solid", borderColor: "grey.400" }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary">
            From: {ticket.student_name} — {ticket.student_email} —{" "}
            {new Date(ticket.created_at).toLocaleDateString("en-GB")}
          </Typography>
          <Typography variant="body2" mt={0.5}>{ticket.message}</Typography>
        </Box>
        <TextField fullWidth label="Your Response" multiline rows={5} value={response} onChange={(e) => setResponse(e.target.value)} sx={{ mb: 2 }} />
        <FormControl fullWidth size="small">
          <InputLabel>Set Status</InputLabel>
          <Select value={newStatus} label="Set Status" onChange={(e) => setNewStatus(e.target.value)}>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="resolved">Resolved</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" size="small">Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" size="small" disabled={loading || !response.trim()} startIcon={loading ? <CircularProgress size={14} /> : <ReplyIcon />}>
          {loading ? "Sending…" : "Send"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ManagerSupport = () => {
  const theme = useTheme();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [respondDialogOpen, setRespondDialogOpen] = useState(false);

  const fetchTickets = () => {
    setLoading(true);
    const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
    getData(`api/support/tickets/${params}`)
      .then((data) => setTickets(Array.isArray(data) ? data : []))
      .catch(() => setTickets([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTickets(); }, [statusFilter]);

  const openCount     = tickets.filter((t) => t.status === "open").length;
  const resolvedCount = tickets.filter((t) => t.status === "resolved").length;

  return (
    <PageLayout>
      <ContentLayout pageTitle="Support Inbox">
        {/* Summary */}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          {[
            { label: "Open",     count: openCount,     color: "warning.main", icon: <PendingIcon /> },
            { label: "Resolved", count: resolvedCount, color: "success.main", icon: <ResolvedIcon /> },
            { label: "Total",    count: tickets.length, color: "primary.main", icon: <InboxIcon /> },
          ].map((s) => (
            <Card key={s.label} elevation={0} sx={{ flex: "1 1 120px", border: "1px solid", borderColor: "divider" }}>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Box sx={{ color: s.color, mb: 0.5 }}>{s.icon}</Box>
                <Typography variant="h5" fontWeight={800} color={s.color}>{s.count}</Typography>
                <Typography variant="caption" color="text.secondary">{s.label}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Toolbar */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Typography variant="h6" fontWeight={700} sx={{ flex: 1 }}>All Support Requests</Typography>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={fetchTickets}>Refresh</Button>
        </Box>

        {/* Ticket list */}
        <Card elevation={0} sx={{ border: "1px solid", borderColor: "divider" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}><CircularProgress /></Box>
          ) : tickets.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <InboxIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
              <Typography color="text.secondary">No support requests found.</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {tickets.map((t, i) => (
                <React.Fragment key={t.id}>
                  {i > 0 && <Divider />}
                  <ListItem
                    alignItems="flex-start"
                    sx={{ px: 3, py: 2, bgcolor: t.status === "open" ? alpha(theme.palette.warning.main, 0.04) : "transparent", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}
                    secondaryAction={
                      <Button size="small" variant={t.status === "open" || t.status === "in_progress" ? "contained" : "outlined"} startIcon={<ReplyIcon />} onClick={() => { setSelectedTicket(t); setRespondDialogOpen(true); }} sx={{ textTransform: "none", fontWeight: 600, mt: 1 }}>
                        {t.status === "open" || t.status === "in_progress" ? "Respond" : "View/Edit"}
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                          <Typography variant="subtitle2" fontWeight={700}>{t.subject}</Typography>
                          <Chip label={t.status.replace("_", " ")} size="small" color={statusColor[t.status] || "default"} />
                        </Box>
                      }
                      secondary={
                        <>
                          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            From: <strong>{t.student_name}</strong> ({t.student_email}) — {new Date(t.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                          </Typography>
                          <Typography variant="body2" color="text.primary" sx={{ maxWidth: "80%" }}>{t.message}</Typography>
                          {t.response && (
                            <Box sx={{ mt: 1, p: 1, bgcolor: "success.50", borderRadius: 1, borderLeft: "3px solid", borderColor: "success.main", maxWidth: "80%" }}>
                              <Typography variant="caption" fontWeight={700} color="success.dark">Replied by {t.responded_by_name}:</Typography>
                              <Typography variant="body2" mt={0.3}>{t.response}</Typography>
                            </Box>
                          )}
                        </>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </Card>

        <RespondDialog ticket={selectedTicket} open={respondDialogOpen} onClose={() => setRespondDialogOpen(false)} onSuccess={fetchTickets} />
      </ContentLayout>
    </PageLayout>
  );
};

export default ManagerSupport;
